import React, { useState, useRef, useEffect } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';

export const BUCKET = 'pos-property-images';

interface ImageUploadProps {
  propertyId: string;
  path: string;
  currentUrl?: string;
  onUploaded: (url: string) => void;
  supabase: SupabaseClient;
  label?: string;
}

/**
 * ImageUpload — upload property images to Supabase Storage.
 *
 * Props:
 *   propertyId  — UUID of the property (used as folder in storage path)
 *   path        — sub-path within the property folder (e.g., "hero", "logo", "gallery/001")
 *   currentUrl  — current image URL (for preview)
 *   onUploaded  — callback(publicUrl) after successful upload
 *   supabase    — Supabase client instance
 *   label       — display label (e.g., "Hero Image")
 */
export default function ImageUpload({ propertyId, path, currentUrl, onUploaded, supabase, label }: ImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(currentUrl || null);
  useEffect(() => { setPreview(currentUrl || null); }, [currentUrl]);
  const [error, setError] = useState<string | null>(null);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [manualUrl, setManualUrl] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Client-side validation
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
    if (!validTypes.includes(file.type)) {
      setError('Only JPEG, PNG, WebP, and AVIF images are accepted.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File must be under 5 MB.');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Determine file extension
      const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const storagePath = `${propertyId}/${path}.${ext}`;

      // Upload (upsert: overwrite if exists)
      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(storagePath, file, { upsert: true, contentType: file.type });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from(BUCKET)
        .getPublicUrl(storagePath);

      const publicUrl = `${urlData.publicUrl}?v=${Date.now()}`;
      setPreview(publicUrl);
      onUploaded(publicUrl);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleManualUrl = (): void => {
    const url = manualUrl.trim();
    if (!url) return;
    setPreview(url);
    onUploaded(url);
    setManualUrl('');
    setShowUrlInput(false);
  };

  const handleRemove = (): void => {
    setPreview(null);
    onUploaded('');
  };

  return (
    <div className="mb-md">
      {label && (
        <label className="form-label" style={{ display: 'block', marginBottom: '0.375rem' }}>
          {label}
        </label>
      )}

      {/* Preview */}
      {preview && (
        <div style={{ position: 'relative', marginBottom: '0.5rem', display: 'inline-block' }}>
          <img
            src={preview}
            alt={label || 'Preview'}
            style={{
              maxWidth: '200px',
              maxHeight: '120px',
              borderRadius: 'var(--radius)',
              objectFit: 'cover',
              border: '1px solid var(--color-border)',
            }}
          />
          <button
            type="button"
            onClick={handleRemove}
            style={{
              position: 'absolute',
              top: '-6px',
              right: '-6px',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              border: 'none',
              background: 'var(--color-error)',
              color: 'var(--color-white)',
              fontSize: 'var(--text-xs)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            }}
            title="Remove image"
          >
            &times;
          </button>
        </div>
      )}

      {/* Upload / URL toggle */}
      <div className="flex gap-sm items-center flex-wrap">
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.375rem',
            padding: '0.375rem 0.75rem',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)',
            fontSize: 'var(--text-md)',
            cursor: uploading ? 'wait' : 'pointer',
            background: 'var(--color-bg-secondary)',
            opacity: uploading ? 0.6 : 1,
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/avif"
            onChange={handleFileSelect}
            disabled={uploading}
            style={{ display: 'none' }}
          />
          {uploading ? 'Uploading...' : (preview ? 'Replace' : 'Upload')}
        </label>

        <button
          type="button"
          onClick={() => setShowUrlInput(!showUrlInput)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--color-text-light)',
            fontSize: 'var(--text-sm)',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          {showUrlInput ? 'Cancel' : 'Use URL instead'}
        </button>
      </div>

      {/* Manual URL input */}
      {showUrlInput && (
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.375rem' }}>
          <input
            type="text"
            className="form-input flex-1"
            value={manualUrl}
            onChange={(e) => setManualUrl(e.target.value)}
            placeholder="https://..."
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleManualUrl(); } }}
          />
          <button type="button" className="btn btn-secondary" onClick={handleManualUrl}
            style={{ fontSize: 'var(--text-md)' }}>Set</button>
        </div>
      )}

      {error && (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-error)', marginTop: '0.25rem' }}>
          {error}
        </div>
      )}
    </div>
  );
}
